import React, { useContext, useMemo } from 'react'
import { TProductPrice } from '../../../../core/types/Product'
import { IOrderBump } from '../../../../core/types/OrderBump'
import { useForm } from 'react-hook-form'
import { useOrderBump } from '../../../../core/hooks/useOrderBump'
import { useProductsSimple } from '../../../../core/hooks/useProductsSimple'
import ProductController from '../../../../core/controllers/ProductController'
import { IErrorResponse } from '../../../../core/types/ErrorResponse'
import OrderBumpController from '../../../../core/controllers/OrderBumpController'
import {
  Box,
  Stack,
  Autocomplete,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  useMediaQuery,
} from '@mui/material'
import SwipeableDrawerRightDialog from '../../../../components/Dialogs/SwipeableDrawerRightDialog'
import LoadingBackdrop from '../../../../components/Loading/LoadingBackdrop'
import SuccessSnackbar from '../../../../components/Snackbar/SuccessSnackbar'
import ErrorSnackbar from '../../../../components/Snackbar/ErrorSnackbar'
import DottedBoxWithBadge from './DottedBoxWithBadge'
import { TUserType } from '../../../../core/types/Auth'
import { AuthContext } from '../../../../core/context/AuthContext'
import NumberFunctions from '../../../../core/functions/NumberFunctions'
import { EditableInput } from '../../../../components/Inputs/EditableInput'

export interface IOrderBumpEditProps {
  id: string
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  execute: () => Promise<void>
}

function OrderBumpsEdit({
  id,
  open,
  setOpen,
  execute,
}: IOrderBumpEditProps) {
  const [sending, setSending] = React.useState(false)
  const [success, setSuccess] = React.useState(false)
  const [error, setError] = React.useState('')
  const [productPrices, setProductPrices] = React.useState<TProductPrice[]>([])

  const { user } = useContext(AuthContext)
  const isAdmin = user?.UserType === TUserType.SystemAdmin

  const isSmallScreen = useMediaQuery((theme: any) =>
    theme.breakpoints.down('md')
  )

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
    watch,
  } = useForm<IOrderBump>({
    defaultValues: {
      id: id,
      productPriceId: '',
      title: '',
      description: '',
      descriptionPrice: '',
      productPrice: {
        id: '',
        name: '',
        value: 0,
        product: {
          id: '',
          name: '',
        },
      },
      hasUnmissableTag: true,
      anchoringPrice: 0,
    },
  })

  //const productId = watch('productPrice.product.id')
  //const description = watch('description')
  //const hasUnmissableTag = watch('hasUnmissableTag')
  //const title = watch('title')
  //const descriptionPrice = watch('descriptionPrice')
  /*const productOfferValue = productPrices.find(
    (x) => x.id === watch('productPriceId')
  )?.value*/
  //const anchoringPrice = watch('anchoringPrice')
  //const productName = watch('productPrice.product.name')
  const { data } = useOrderBump({ id: id || '' })
  const { data: products, loading: loadingProducts } = useProductsSimple({
    initialExecute: true,
  })

  const values = useMemo(
    () => ({
      productId: watch('productPrice.product.id'),
      description: watch('description'),
      hasUnmissableTag: watch('hasUnmissableTag'),
      title: watch('title'),
      descriptionPrice: watch('descriptionPrice'),
      anchoringPrice: watch('anchoringPrice'),
      productName: watch('productPrice.product.name'),
      productPriceId: watch('productPriceId'),
      productOfferValue: productPrices.find(
        (x) => x.id === watch('productPriceId')
      )?.value,
    }),
    [
      productPrices,
      watch('productPrice.product.id'),
      watch('description'),
      watch('hasUnmissableTag'),
      watch('title'),
      watch('descriptionPrice'),
      watch('anchoringPrice'),
      watch('productPrice.product.name'),
      watch('productPriceId'),
    ]
  )

  const getOrderBumpPricesByProduct = async () => {
    const { productId } = values
    if (productId && productId !== null && productId !== '') {
      setSending(true)
      try {
        const response = await ProductController.getOrderBumpPricesByProduct({
          productId,
          orderBumpId: id ?? null,
        })
        const err = response as IErrorResponse
        if (err.code) {
          setError(err.error)
        } else {
          setProductPrices(response as TProductPrice[])
        }
      } finally {
        setSending(false)
      }
    }
  }

  React.useEffect(() => {
    if (data !== null) {
      reset(data)
    }
    return () => {
      if (data !== null) {
        reset(data)
      }
    }
  }, [data, reset])

  React.useEffect(() => {
    getOrderBumpPricesByProduct()
    return () => {}
  }, [values.productId])

  const Submit = async (data: any) => {
    setSending(true)
    setSuccess(false)
    setError('')
    try {
      const response = await OrderBumpController.update({ data })

      if (!response.success) {
        setError(response.error)
      } else {
        setSuccess(true)
        setOpen(false)
        execute()
      }
    } finally {
      setSending(false)
    }
  }

  return (
    <Box>
      <SwipeableDrawerRightDialog
        open={open}
        onClose={() => setOpen(false)}
        setOpen={setOpen}
        title="Editar Order Bump"
        subtitle="Preencha os dados corretamente"
        buttons={
          isAdmin
            ? []
            : [
                {
                  title: 'Cancelar',
                  onClick: () => setOpen(false),
                  type: 'negative',
                },
                {
                  title: 'Salvar',
                  type: 'positive',
                  event: 'submit',
                },
              ]
        }
        noValidate
        component="form"
        onSubmit={handleSubmit(Submit)}
        maxWidth="lg"
      >
        <Box width="100%">
          <Box sx={{ width: '100%', margin: '0 auto' }}>
            <Box sx={{ width: '100%', alignItems: 'center' }}>
              <Stack
                direction={isSmallScreen ? 'column' : 'row'}
                pt={2}
                spacing={3}
                sx={{
                  width: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                gap="32px"
              >
                <Stack spacing={3} width={isSmallScreen ? '100%' : '50%'}>
                  <Autocomplete
                    fullWidth
                    disablePortal
                    disabled={isAdmin}
                    options={products}
                    getOptionLabel={(option) => option.name}
                    onChange={(event, option) => {
                      if (option && option !== null) {
                        setValue('productPrice.product.id', option.id)
                        setValue('productPrice.product.name', option.name)
                        setValue('productPriceId', '')
                      }
                    }}
                    value={{
                      //id: watch('productPrice.product.id'),
                      //name: watch('productPrice.product.name'),
                      id: values.productId,
                      name: values.productName,
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Produto"
                        {...register('productPrice.product.name')}
                      />
                    )}
                  />
                  <FormControl fullWidth disabled={productPrices.length <= 0}>
                    <InputLabel id="product-select-label">
                      Oferta do Produto
                    </InputLabel>
                    <Select
                      variant="outlined"
                      disabled={isAdmin}
                      labelId="product-select-label"
                      label="Oferta do Produto"
                      //value={watch('productPriceId')}
                      value={values.productPriceId}
                      {...register('productPriceId', { required: true })}
                      error={errors.productPriceId?.type === 'required'}
                    >
                      {productPrices.map((price) => (
                        <MenuItem value={price.id}>
                          {NumberFunctions.formatMoneyDefault(price.value)} -{' '}
                          {price.offerName}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.productPriceId?.type === 'required' && (
                      <Typography variant="overline" color={'red'}>
                        Obrigatório
                      </Typography>
                    )}
                  </FormControl>
                  <Box width="100%">
                    <TextField
                      fullWidth
                      multiline
                      disabled={isAdmin}
                      rows={2}
                      type="text"
                      label="Título"
                      {...register('title', { required: true })}
                      inputProps={{ maxLength: 150 }}
                      InputLabelProps={{ shrink: true }}
                      error={errors.title?.type === 'required'}
                    />
                    {errors.title?.type === 'required' && (
                      <Typography variant="overline" color={'red'}>
                        Título é um campo obrigatório
                      </Typography>
                    )}
                  </Box>
                  {data?.descriptionPrice && (
                    <Box>
                      <TextField
                        fullWidth
                        multiline
                        disabled
                        type="text"
                        label="Descrição do Preço"
                        {...register('descriptionPrice', { required: true })}
                        inputProps={{ maxLength: 255 }}
                        InputLabelProps={{ shrink: true }}
                        error={errors.descriptionPrice?.type === 'required'}
                      />
                      {errors.descriptionPrice?.type === 'required' && (
                        <Typography variant="overline" color={'red'}>
                          Descrição do Preço obrigatório
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Box>
                    <TextField
                      fullWidth
                      disabled={isAdmin}
                      type="number"
                      label="Preço de ancoragem"
                      {...register('anchoringPrice', { required: true })}
                      InputProps={{
                        inputProps: { min: 5 },
                        startAdornment: 'R$',
                      }}
                      error={errors.anchoringPrice?.type === 'required'}
                    />
                    {errors.anchoringPrice?.type === 'required' && (
                      <Typography variant="overline" color={'red'}>
                        Preço de ancoragem obrigatório
                      </Typography>
                    )}
                  </Box>

                  <Stack gap="8px">
                    <Box>
                      <EditableInput
                        title="Descrição"
                        value={values.description ?? ''}
                        setValue={(value) => setValue('description', value)}
                      />
                      {errors.description?.type === 'required' && (
                        <Typography variant="overline" color={'red'}>
                          Descrição é um campo obrigatório
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      color="#707070"
                      fontSize="12px"
                      fontWeight="400"
                    >
                      Descrição que aparece para seus compradores
                    </Typography>
                  </Stack>

                  {/* <Stack>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="medium"
                          disabled={isAdmin}
                          checked={watch('hasUnmissableTag') ?? true}
                          onChange={(e) => {
                            setValue('hasUnmissableTag', e.target.checked)
                          }}
                        />
                      }
                      label={
                        <Typography
                          lineHeight="18px"
                          fontSize={{ xs: '13px', sm: '14px' }}
                          fontWeight={400}
                          sx={{
                            color: '#5D5E61',
                          }}
                        >
                          Adicionar tag “Imperdível”
                        </Typography>
                      }
                    />
                  </Stack> */}
                </Stack>

                <Box
                  sx={{
                    width: isSmallScreen ? '100%' : '50%',
                    minWidth: 0,
                    overflow: 'hidden',
                  }}
                >
                  <DottedBoxWithBadge
                    description={values.description}
                    hasUnmissableTag={values.hasUnmissableTag}
                    productOfferValue={values.productOfferValue}
                    descriptionPrice={values.descriptionPrice}
                    imgURL={data?.imageProductUrl}
                    title={values.title}
                    anchoringPrice={values.anchoringPrice}
                    productName={values.productName}
                  />
                </Box>
              </Stack>
            </Box>
          </Box>
        </Box>
      </SwipeableDrawerRightDialog>
      <LoadingBackdrop open={sending || loadingProducts} />

      <ErrorSnackbar open={error !== ''} onClose={() => setError('')}>
        {error}
      </ErrorSnackbar>

      <SuccessSnackbar
        open={success}
        onClose={() => {
          setSuccess(false)
          setOpen(false)
        }}
      >
        Alterado com sucesso
      </SuccessSnackbar>
    </Box>
  )
}

export default OrderBumpsEdit
